import { useEffect, useState } from 'react';
import { TransactionHistoryItem } from '@lunit/shared-types';
import { getUserTransactions } from '@core/api/users';
import { useUser } from '@core/contexts/ProfileContext';
import { useVenue } from '@core/contexts/VenueContext';
import { Card } from '@core/components/Card';
import { Skeleton } from '@core/components/Skeleton';
import { ArrowUpCircle, ArrowDownCircle, AlertCircle } from 'lucide-react';
import { Header } from '@core/components/Header';

const CREDIT_TYPES = new Set(['EARN', 'BONUS', 'REFERRAL_BONUS', 'BIRTHDAY_BONUS']);

const isCredit = (tx: { type: string; amount: number }): boolean =>
  CREDIT_TYPES.has(tx.type) || (tx.type === 'ADJUSTMENT' && tx.amount > 0);

export function HistoryPage() {
  const { user } = useUser();
  const { venue } = useVenue();
  const [transactions, setTransactions] = useState<TransactionHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user && venue) {
      loadHistory();
    } else if (user && !venue) {
      // Если пользователь есть, но venue еще загружается
      setLoading(true);
    } else if (!user) {
      setLoading(false);
    }
  }, [user, venue]);

  const loadHistory = async () => {
    if (!user || !venue) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getUserTransactions(user.id, venue.id);
      setTransactions(data);
    } catch (err) {
      console.error('Failed to load history:', err);
      setError('Не удалось загрузить историю операций');
      // Ошибка уже показана через toast в API-клиенте
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        <Header />
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Header />
        <Card className="text-center py-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-500" />
          <p className="text-text-main opacity-80 mb-4">{error}</p>
          <button
            onClick={loadHistory}
            className="px-4 py-2 bg-primary text-white rounded-2xl hover:opacity-90 transition-opacity"
          >
            Попробовать снова
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      <Header showBack={true} />

      {transactions.length === 0 ? (
        <Card className="text-center py-8 text-text-main opacity-60">
          История пуста
        </Card>
      ) : (
        transactions.map((tx) => (
          <Card key={tx.id}>
            <div className="flex items-center gap-3">
              {isCredit(tx) ? (
                <ArrowUpCircle
                  className="w-8 h-8 flex-shrink-0"
                  style={{ color: 'var(--brand-accent)' }}
                />
              ) : (
                <ArrowDownCircle className="w-8 h-8 text-red-500 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate text-text-main">{tx.description || 'Операция'}</div>
                <div className="text-sm text-text-main opacity-60">
                  {new Date(tx.createdAt).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              <div
                className={`font-semibold text-lg ${
                  isCredit(tx) ? 'text-primary' : 'text-red-500'
                }`}
              >
                {isCredit(tx) ? '+' : '-'}
                {Math.abs(tx.amount)}
              </div>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}


